import { useMemo, useState } from "react";
import { CheckSquare, Square, ListChecks, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { getProjectSubsystems3D } from "@/lib/projectSubsystems3d";

const DEFAULT_AGENT_ITEMS = [
  "Revisar despiece",
  "Validar materiales",
  "Continuar en chat",
] as const;

export type ArtifactBomChecklistProps = {
  projectTitle: string;
  className?: string;
};

export function ArtifactBomChecklist({ projectTitle, className }: ArtifactBomChecklistProps) {
  const subsystems = useMemo(() => getProjectSubsystems3D(projectTitle), [projectTitle]);

  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(DEFAULT_AGENT_ITEMS.map((label) => [label, false]))
  );

  const toggle = (label: string) => {
    setChecked((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className={cn("space-y-4 font-mono-tech", className)}>
      <section>
        <div className="flex items-center gap-1.5 mb-2">
          <Package className="w-3.5 h-3.5 text-emerald-400" />
          <h3 className="text-[10px] uppercase tracking-[0.14em] text-emerald-400 font-bold">
            BOM · Materiales
          </h3>
        </div>
        <ul className="space-y-1.5">
          {subsystems.map((sub) => (
            <li
              key={sub.step}
              className="rounded-lg border border-border/60 bg-card/40 px-2.5 py-2 text-[11px]"
            >
              <div className="flex items-start gap-2">
                <span
                  className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: sub.color }}
                />
                <div className="min-w-0">
                  <p className="font-bold text-foreground leading-snug truncate">{sub.name}</p>
                  <p className="text-muted-foreground mt-0.5 leading-snug">{sub.material}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <div className="flex items-center gap-1.5 mb-2">
          <ListChecks className="w-3.5 h-3.5 text-primary" />
          <h3 className="text-[10px] uppercase tracking-[0.14em] text-primary font-bold">
            Checklist agente
          </h3>
        </div>
        <ul className="space-y-1">
          {DEFAULT_AGENT_ITEMS.map((label) => {
            const done = !!checked[label];
            return (
              <li key={label}>
                <button
                  type="button"
                  onClick={() => toggle(label)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-[12px] transition-colors cursor-pointer",
                    done
                      ? "border-primary/30 bg-primary/10 text-muted-foreground"
                      : "border-border/60 bg-card/40 text-foreground hover:bg-muted/30"
                  )}
                >
                  {done ? (
                    <CheckSquare className="w-3.5 h-3.5 text-primary shrink-0" />
                  ) : (
                    <Square className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  )}
                  <span className={cn(done && "line-through")}>{label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
